import React from "react";
import { useHistory } from "react-router-dom";
import images from "../../utilities/images/images";
import {
  extractTransactionId,
  isPaymentCompleted,
  isPaymentPending,
  isPaymentUnsuccessfulUi,
} from "./paytmHelpers";
import "./PaytmPaymentStatus.scss";

const DEFAULT_SUPPORT = {
  email: "info@motion.ac.in",
  phone: "18002704700",
};

const SuccessIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 24L21 31L34 17"
      stroke="white"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FailedIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16 16L32 32M32 16L16 32"
      stroke="white"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

const PendingIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="14" stroke="white" strokeWidth="3" />
    <path
      d="M24 14V24L30 27"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 3V15M12 15L7 10M12 15L17 10"
      stroke="#334155"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17"
      stroke="#334155"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const formatAmount = (receipt, transactionData, isSuccess) => {
  if (receipt?.amount_formatted) return receipt.amount_formatted;
  const amount = receipt?.amount ?? transactionData?.txn_amount;
  if (amount == null || amount === "") return "--------";
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return String(amount);
  return `₹${numeric.toLocaleString("en-IN")}`;
};

const formatPurchaseDate = (receipt, transactionData) => {
  const raw =
    receipt?.purchase_date ||
    receipt?.purchase_date_iso ||
    transactionData?.txn_date ||
    "";

  if (!raw) return "--------";

  const trimmed = String(raw).trim();
  if (/^[A-Za-z]+\s+\d{1,2},\s*\d{4}$/.test(trimmed)) {
    return trimmed;
  }

  const datePart = trimmed.split(/[T ]/)[0];
  const parsed = new Date(datePart.includes("-") ? datePart : trimmed);

  if (Number.isNaN(parsed.getTime())) {
    return datePart || "--------";
  }

  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const PaytmPaymentStatus = ({
  paytmResponse,
  transactionData,
  onDownloadReceipt,
  isReceiptLoading,
  isPendingVerification = false,
  onCheckStatusAgain,
  isCheckingStatus = false,
  successPrimaryLabel = "Access Course",
  onSuccessPrimary,
  onTryAgain,
  onReturnToStore,
  onBack,
  onGoToDashboard,
}) => {
  const history = useHistory();
  const isSuccess = isPaymentCompleted(paytmResponse);
  const isFailed = !isSuccess && isPaymentUnsuccessfulUi(paytmResponse);
  const isPending =
    !isSuccess &&
    !isFailed &&
    (isPendingVerification || isPaymentPending(paytmResponse));
  const receipt =
    paytmResponse?.receipt || paytmResponse?.data?.receipt || {};
  const orderStatus =
    paytmResponse?.orderStatus || paytmResponse?.data?.orderStatus || {};
  const recommendedPackages =
    paytmResponse?.recommended_packages ||
    paytmResponse?.data?.recommended_packages ||
    [];
  const support = receipt?.support || DEFAULT_SUPPORT;

  const packageId =
    orderStatus?.package_id || transactionData?.package_id || "";

  // Prefer status API transaction id only; never show order_id here.
  const transactionId = extractTransactionId(paytmResponse) || "--------";

  // Pending must not show receipt "Payment Unsuccessful" while paymentStatus is Pending.
  const heading = isPending
    ? "Pending Verification"
    : receipt?.heading ||
      (isSuccess ? "Payment Successful!" : "Payment Failed!");

  const subMessage = isPending
    ? "Your payment is still being verified. This can take a few minutes."
    : receipt?.sub_message ||
      receipt?.sub_heading ||
      (isSuccess
        ? "Your payment has been processed successfully. You will receive a confirmation email shortly."
        : "We couldn't process your payment. If money was debited, it will be refunded within 3-5 days.");

  const transactionRows = [
    {
      label: "Transaction ID",
      value: transactionId,
      valueClass: "value--brand",
    },
    {
      label: "Course",
      value: receipt?.plan_name || transactionData?.package_name || "--------",
      valueClass: "value--course",
    },
    {
      label: "Duration",
      value:
        receipt?.duration || transactionData?.package_duration || "--------",
    },
    {
      label: "Purchase Date",
      value: formatPurchaseDate(receipt, transactionData),
    },
    {
      label: "Merchant",
      value: receipt?.merchant_name || "Motion",
    },
  ];

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    history.push("/dashboard");
  };

  const handleReturnToStore = () => {
    if (onReturnToStore) {
      onReturnToStore();
      return;
    }
    history.push("/store");
  };

  const handleTryAgain = () => {
    if (onTryAgain) {
      onTryAgain(packageId);
      return;
    }
    if (packageId) {
      history.push(`/package/${packageId}`);
      return;
    }
    history.push("/store");
  };

  const handlePackageClick = (id) => {
    if (id) history.push(`/package/${id}`);
  };

  const handleAccessCourse = () => {
    if (onSuccessPrimary) {
      onSuccessPrimary();
      return;
    }
    history.push("/dashboard");
  };

  const handleGoToDashboard = () => {
    if (onGoToDashboard) {
      onGoToDashboard();
      return;
    }
    history.push("/dashboard");
  };

  return (
    <div className="paytm-payment-status-page">
      <div className="paytm-payment-status-header">
        <button
          type="button"
          className="back-btn"
          onClick={handleBack}
          aria-label="Go back"
        >
          <img src={images.new_backarrow} alt="" />
        </button>
      </div>

      <div
        className={`paytm-payment-status-layout${
          recommendedPackages.length === 0
            ? " paytm-payment-status-layout--single"
            : ""
        }`}
      >
        <div className="paytm-payment-status-card">
          <div className="status-icon-wrap">
            <div
              className={`status-icon ${
                isSuccess
                  ? "status-icon--success"
                  : isPending
                  ? "status-icon--pending"
                  : "status-icon--failed"
              }`}
            >
              {isSuccess ? (
                <SuccessIcon />
              ) : isPending ? (
                <PendingIcon />
              ) : (
                <FailedIcon />
              )}
            </div>
          </div>

          <h1
            className={`status-heading ${
              isSuccess
                ? "status-heading--success"
                : isPending
                ? "status-heading--pending"
                : "status-heading--failed"
            }`}
          >
            {heading}
          </h1>

          <p className="status-submessage">{subMessage}</p>

          {isSuccess ? (
            <div className="order-receipt-header">
              <h2 className="order-receipt-title">Order Receipt</h2>
              <div className="receipt-action-btns">
                <button
                  type="button"
                  className="receipt-icon-btn"
                  onClick={onDownloadReceipt}
                  disabled={isReceiptLoading}
                  aria-label="Download receipt"
                >
                  <DownloadIcon />
                </button>
              </div>
            </div>
          ) : (
            <h2 className="transaction-details-title">Transaction Details</h2>
          )}

          <div className="transaction-details-box">
            <div className="transaction-row">
              <span className="label">Amount</span>
              <span
                className={
                  isSuccess
                    ? "value value--amount-success"
                    : isPending
                    ? "value value--amount-pending"
                    : "value value--amount-failed"
                }
              >
                {formatAmount(receipt, transactionData, isSuccess)}
              </span>
            </div>

            <div className="transaction-divider" />

            <div className="transaction-rows-group">
              {transactionRows.map((row) => (
                <div className="transaction-row" key={row.label}>
                  <span className="label">{row.label}</span>
                  <span className={`value ${row.valueClass || ""}`}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="status-actions">
            {isSuccess ? (
              <button
                type="button"
                className="btn-primary"
                onClick={handleAccessCourse}
              >
                {successPrimaryLabel}
              </button>
            ) : isPending ? (
              <button
                type="button"
                className="btn-primary btn-primary--pending"
                onClick={onCheckStatusAgain}
                disabled={isCheckingStatus}
              >
                {isCheckingStatus ? "Checking..." : "Check Status Again"}
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={handleTryAgain}
              >
                Try Again
              </button>
            )}
            {isPending ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleGoToDashboard}
              >
                Go to Dashboard
              </button>
            ) : (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleReturnToStore}
              >
                Return to store
              </button>
            )}
          </div>

          <div className="support-footer">
            <p>Need help? Talk to our support team.</p>
            <p>
              Email us :{" "}
              <span className="support-highlight">{support.email}</span>
            </p>
            <p>
              Call us :{" "}
              <span className="support-highlight">{support.phone}</span>
            </p>
          </div>
        </div>

        {recommendedPackages.length > 0 && (
          <aside className="recommended-packages-sidebar">
            <h3 className="sidebar-title">Explore other courses</h3>
            {recommendedPackages.map((pkg) => (
              <div
                key={pkg.package_id}
                className="recommended-package-card"
                onClick={() => handlePackageClick(pkg.package_id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handlePackageClick(pkg.package_id);
                }}
                role="button"
                tabIndex={0}
              >
                {pkg.icon ? (
                  <img src={pkg.icon} alt="" />
                ) : (
                  <div className="recommended-package-placeholder" />
                )}
                <span>{pkg.package_name}</span>
              </div>
            ))}
          </aside>
        )}
      </div>
    </div>
  );
};

export default PaytmPaymentStatus;
